import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ChefHat, 
  ArrowLeft,
  Save,
  CheckCircle2,
  Package,
  FileText,
  Clock,
  AlertCircle,
  Scale,
  Plus,
  Minus
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export const KitchenDetailPage = () => {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [production, setProduction] = useState(null);
  const [handover, setHandover] = useState(null);
  const [handoverItems, setHandoverItems] = useState([]);
  const [itemValues, setItemValues] = useState({});
  const [notes, setNotes] = useState('');
  
  // Tare management
  const [tareItems, setTareItems] = useState([]);
  const [itemTare, setItemTare] = useState({}); // { item_id: { tareItemId: count, ... } }
  const [tareDialogOpen, setTareDialogOpen] = useState(false);
  const [selectedItemForTare, setSelectedItemForTare] = useState(null);
  const [tempTareSelection, setTempTareSelection] = useState({});

  useEffect(() => {
    fetchData();
    fetchTareItems();
  }, [recordId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/kitchen/handover/${recordId}`);
      
      setProduction(response.data.production);
      setHandover(response.data.handover);
      setHandoverItems(response.data.handover_items || []);
      
      // Initialize item values from existing handover or empty
      const existingItems = response.data.handover?.items || [];
      const initialValues = {};
      const initialTare = {};
      
      response.data.handover_items?.forEach(item => {
        const existing = existingItems.find(e => e.item_id === item.id);
        initialValues[item.id] = existing?.quantity || '';
        // Load tare data if exists
        if (existing?.tare_items) {
          initialTare[item.id] = existing.tare_items;
        }
      });
      setItemValues(initialValues);
      setItemTare(initialTare);
      setNotes(response.data.handover?.notes || '');
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Chyba při načítání dat');
      navigate('/kuchyne');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTareItems = async () => {
    try {
      const response = await api.get('/kitchen/tare-items');
      setTareItems(response.data || []);
    } catch (error) {
      console.error('Error fetching tare items:', error);
    }
  };

  // Calculate total tare weight for an item
  const calculateTareWeight = (itemId) => {
    const tare = itemTare[itemId] || {};
    let totalWeight = 0;
    Object.entries(tare).forEach(([tareItemId, count]) => {
      const tareItem = tareItems.find(t => t.id === tareItemId);
      if (tareItem && count > 0) {
        totalWeight += tareItem.weight * count;
      }
    });
    return totalWeight;
  };
  
  // Calculate net weight for an item
  const calculateNetWeight = (itemId) => {
    const grossWeight = parseFloat(itemValues[itemId]) || 0;
    const tareWeight = calculateTareWeight(itemId);
    return Math.max(0, grossWeight - tareWeight);
  };
  
  // Open tare dialog for an item
  const openTareDialog = (item) => {
    setSelectedItemForTare(item);
    setTempTareSelection(itemTare[item.id] || {});
    setTareDialogOpen(true);
  };
  
  // Update tare count in temp selection
  const updateTareCount = (tareItemId, delta) => {
    setTempTareSelection(prev => {
      const current = prev[tareItemId] || 0;
      const newCount = Math.max(0, current + delta);
      if (newCount === 0) {
        const { [tareItemId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [tareItemId]: newCount };
    });
  };
  
  // Save tare selection
  const saveTareSelection = () => {
    if (selectedItemForTare) {
      setItemTare(prev => ({
        ...prev,
        [selectedItemForTare.id]: tempTareSelection
      }));
    }
    setTareDialogOpen(false);
    setSelectedItemForTare(null);
  };
  
  // Check if all items have tare configured
  const allItemsHaveTare = () => {
    return handoverItems.every(item => {
      const value = parseFloat(itemValues[item.id]);
      if (!value || value <= 0) return true; // Empty items don't need tare
      const tare = itemTare[item.id] || {};
      return Object.values(tare).some(count => count > 0);
    });
  };

  const handleSave = async (confirm = false) => {
    // Check if tare is configured for all items with values
    if (confirm && !allItemsHaveTare()) {
      toast.error('Pro potvrzení musíte u všech položek zadat táru (nádoby/vozíky)');
      return;
    }
    
    try {
      setSaving(true);
      
      const items = handoverItems.map(item => {
        const grossWeight = parseFloat(itemValues[item.id]) || 0;
        const tareWeight = calculateTareWeight(item.id);
        const netWeight = Math.max(0, grossWeight - tareWeight);
        
        return {
          item_id: item.id,
          name: item.name,
          unit: item.unit,
          quantity: grossWeight, // Original gross weight
          tare_items: itemTare[item.id] || {},
          tare_weight: tareWeight,
          net_weight: netWeight
        };
      }).filter(item => item.quantity > 0);
      
      await api.post('/kitchen/handover', {
        production_record_id: recordId,
        items,
        notes,
        confirmed: confirm
      });
      
      if (confirm) {
        toast.success('Předávka potvrzena a odeslána na balírnu');
        navigate('/kuchyne');
      } else {
        toast.success('Předávka uložena');
        fetchData();
      }
    } catch (error) {
      console.error('Error saving handover:', error);
      toast.error('Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  const isConfirmed = handover?.confirmed;

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/kuchyne')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ChefHat className="w-7 h-7 text-orange-600" />
              Předávka pro balírnu
            </h1>
            <p className="text-slate-500 mt-1">{production?.doklad}</p>
          </div>
          {isConfirmed ? (
            <Badge className="bg-green-100 text-green-700 border-green-200 text-sm px-3 py-1">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Předáno
            </Badge>
          ) : (
            <Badge variant="outline" className="text-orange-600 border-orange-300 text-sm px-3 py-1">
              <Clock className="w-4 h-4 mr-1" />
              Čeká na předání
            </Badge>
          )}
        </div>

        {/* Production info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Informace o výrobě
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-500">Výrobek</p>
                <p className="font-medium">{production?.data?.Popis || production?.data?.Výrobek || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Šarže</p>
                <p className="font-medium">{production?.data?.Šarže || production?.data?.['Číslo šarže'] || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Plánované množství</p>
                <p className="font-medium text-blue-600">{production?.data?.['Množ.plán.'] || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Skutečné množství</p>
                <p className="font-medium">{production?.data?.['Skut. množství'] || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Stav</p>
                <p className="font-medium">{production?.data?.Stav || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Směna</p>
                <p className="font-medium">{production?.data?.Směna || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Handover form */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5" />
              Předávka - co předáváte balírně?
            </CardTitle>
          </CardHeader>
          <CardContent>
            {handoverItems.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nejsou nastaveny žádné položky předávky.</p>
                <p className="text-sm">Nastavte je v Administraci → Kuchyně</p>
              </div>
            ) : (
              <div className="space-y-4">
                {handoverItems.sort((a, b) => a.order - b.order).map((item) => {
                  const grossWeight = parseFloat(itemValues[item.id]) || 0;
                  const tareWeight = calculateTareWeight(item.id);
                  const netWeight = calculateNetWeight(item.id);
                  const hasTare = Object.values(itemTare[item.id] || {}).some(c => c > 0);
                  
                  return (
                    <div key={item.id} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-4 mb-2">
                        <label className="w-40 text-sm font-medium text-slate-700">
                          {item.name}
                        </label>
                        <div className="flex-1 flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Celk. hmotnost"
                            value={itemValues[item.id] || ''}
                            onChange={(e) => setItemValues(prev => ({
                              ...prev,
                              [item.id]: e.target.value
                            }))}
                            disabled={isConfirmed}
                            className="max-w-32"
                            data-testid={`handover-item-${item.id}`}
                          />
                          <span className="text-sm text-slate-500 w-12">{item.unit}</span>
                          <Button
                            type="button"
                            variant={hasTare ? "default" : "outline"}
                            size="sm"
                            onClick={() => openTareDialog(item)}
                            disabled={isConfirmed}
                            className={hasTare ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300 text-orange-600 hover:bg-orange-50"}
                          >
                            <Scale className="w-4 h-4 mr-1" />
                            Nádoby
                          </Button>
                        </div>
                      </div>
                      
                      {/* Show tare and net weight if values exist */}
                      {grossWeight > 0 && (
                        <div className="ml-44 flex items-center gap-4 text-sm">
                          <span className="text-slate-500">
                            Zadáno: <span className="font-medium text-slate-700">{grossWeight.toFixed(2)} {item.unit}</span>
                          </span>
                          {tareWeight > 0 && (
                            <>
                              <span className="text-slate-400">−</span>
                              <span className="text-slate-500">
                                Tára: <span className="font-medium text-red-600">{tareWeight.toFixed(2)} {item.unit}</span>
                              </span>
                              <span className="text-slate-400">=</span>
                            </>
                          )}
                          <span className={tareWeight > 0 ? "text-green-700 font-semibold" : "text-slate-500"}>
                            {tareWeight > 0 ? 'Čistá hmotnost: ' : ''}
                            <span className={tareWeight > 0 ? "text-green-700 font-bold" : "font-medium"}>
                              {netWeight.toFixed(2)} {item.unit}
                            </span>
                          </span>
                        </div>
                      )}
                      
                      {/* Show tare items summary */}
                      {hasTare && (
                        <div className="ml-44 mt-1 text-xs text-slate-500">
                          {Object.entries(itemTare[item.id] || {}).map(([tareId, count]) => {
                            if (count <= 0) return null;
                            const tareItem = tareItems.find(t => t.id === tareId);
                            if (!tareItem) return null;
                            return (
                              <span key={tareId} className="mr-3">
                                {count}× {tareItem.name} ({(tareItem.weight * count).toFixed(1)} kg)
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Poznámky
              </label>
              <Textarea
                placeholder="Volitelné poznámky k předávce..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isConfirmed}
                rows={3}
                data-testid="handover-notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Balírna verification info */}
        {handover?.balirna_verified && (
          <Card className="mb-6 border-blue-200 bg-blue-50/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                <CheckCircle2 className="w-5 h-5" />
                Balírna převzala
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                Převzal: {handover.balirna_verified_by_name} dne {new Date(handover.balirna_verified_at).toLocaleDateString('cs-CZ')}
              </p>
              {handover.balirna_corrections?.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-slate-700">Korekce od balírny:</p>
                  <div className="mt-1 space-y-1">
                    {handover.balirna_corrections.map((corr, idx) => (
                      <p key={idx} className="text-sm text-slate-600">
                        {corr.name}: přijato {corr.received_quantity} {corr.unit}
                        {corr.original_quantity !== corr.received_quantity && (
                          <span className="text-orange-600"> (původně: {corr.original_quantity})</span>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              {handover.balirna_notes && (
                <p className="mt-2 text-sm text-slate-600">
                  Poznámka: {handover.balirna_notes}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {!isConfirmed && (
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              <Save className="w-4 h-4 mr-2" />
              Uložit rozpracované
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {saving ? 'Ukládám...' : 'Potvrdit a předat'}
            </Button>
          </div>
        )}
      </div>

      {/* Tare Selection Dialog */}
      <Dialog open={tareDialogOpen} onOpenChange={setTareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-orange-600" />
              Tára pro: {selectedItemForTare?.name}
            </DialogTitle>
            <DialogDescription>
              Zadejte počet nádob a vozíků, které se odečtou od celkové hmotnosti
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {tareItems.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                Nejsou definovány žádné položky táry.<br/>
                Nastavte je v Administraci → Kuchyně
              </p>
            ) : (
              tareItems.map((tareItem) => {
                const count = tempTareSelection[tareItem.id] || 0;
                const subtotal = count * tareItem.weight;
                
                return (
                  <div key={tareItem.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{tareItem.name}</p>
                      <p className="text-sm text-slate-500">{tareItem.weight} kg / ks</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => updateTareCount(tareItem.id, -1)}
                        disabled={count <= 0}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-semibold">{count}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => updateTareCount(tareItem.id, 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {subtotal > 0 && (
                      <span className="text-sm font-medium text-orange-600 w-20 text-right">
                        {subtotal.toFixed(1)} kg
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
          
          {/* Total tare summary */}
          {Object.values(tempTareSelection).some(c => c > 0) && (
            <div className="border-t pt-3">
              <div className="flex justify-between items-center text-lg font-semibold">
                <span>Celková tára:</span>
                <span className="text-orange-600">
                  {Object.entries(tempTareSelection).reduce((sum, [id, count]) => {
                    const item = tareItems.find(t => t.id === id);
                    return sum + (item ? item.weight * count : 0);
                  }, 0).toFixed(2)} kg
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTareDialogOpen(false)}>
              Zrušit
            </Button>
            <Button onClick={saveTareSelection} className="bg-orange-600 hover:bg-orange-700">
              Potvrdit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default KitchenDetailPage;
